'use client'
import { SanityImageSource } from '@sanity/image-url'
import Image from 'next/image'
import Link from 'next/link'
import { isUkSite } from '@/lib/config/site'
import { cn } from '@/lib/utils/utils'
import { urlFor } from '@/sanity/lib/image'
import type { SITE_SETTINGS_QUERY_RESULT } from '@/sanity/types'
import { Button } from '../../../common/button'
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from '../../../common/navigation-menu'
import RegisterInterestForm from '../../../features/BlockContent/HeroSliderBanner/RegisterInterest'
import CalendlyFlowPopup from '../../../features/Calendly/CalendlyFlowPopup'
import MastHeadMobile from './MastHeadMobile'

type MastheadNavigationType =
  NonNullable<SITE_SETTINGS_QUERY_RESULT>['mastheadNavigation']

export type MastHeadRendererHoveringProps = {
  logo: SanityImageSource & { altText?: string }
  mastheadNavigation: MastheadNavigationType
  careers?: boolean
}

function MastHeadHoveringDesktop({
  logo,
  mastheadNavigation,
  careers,
}: {
  logo: SanityImageSource & { altText?: string }
  mastheadNavigation: MastheadNavigationType
  careers?: boolean
}) {
  const {
    navigation,
    showContactUs,
    showCareers,
    showFamilyHub,
    showCalendlyFlow,
    contactForm,
  } = mastheadNavigation

  return (
    <div className="mx-auto w-full max-w-[1472px]">
      <div
        className={cn(
          'flex h-full w-full items-center justify-between gap-6 rounded-4xl bg-primary-blue shadow-lg px-5 xl:px-14'
        )}
      >
        <Link aria-label="Home" href={careers ? '/careers' : '/'}>
          <Image
            className="w-16"
            src={urlFor(logo).width(600).height(600).url()}
            width={600}
            height={600}
            alt={logo.altText || 'logo'}
          />
        </Link>
        <NavigationMenu viewport={false} className="z-30 py-3">
          <NavigationMenuList className="flex gap-1">
            {navigation?.map((item, index) => {
              const itemId = (item as { _id?: string })._id
              const baseKey =
                itemId ||
                `nav-${item._type ?? 'item'}-${item.title ?? ''}-${index}`

              if (item._type === 'navigationItem') {
                return (
                  <NavigationMenuItem key={baseKey}>
                    <NavigationMenuLink
                      asChild
                      className={cn(
                        navigationMenuTriggerStyle(),
                        'hover:text-primary-pink'
                      )}
                    >
                      <Link href={item?.url || ''}>{item.title}</Link>
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                )
              }

              return (
                <NavigationMenuItem key={baseKey}>
                  <NavigationMenuTrigger title={item.title || ''}>
                    {item.link?.url ? (
                      <Link href={item.link?.url || ''}>{item.title}</Link>
                    ) : (
                      <>{item.title}</>
                    )}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div
                      className={cn(
                        'grid gap-2',
                        (item.items?.length ?? 0) > 4
                          ? 'grid-cols-2'
                          : 'grid-cols-1'
                      )}
                    >
                      {item.items?.map((subItem, subIndex) => {
                        const subItemId = (subItem as { _id?: string })._id
                        const subKey =
                          subItemId ||
                          `sub-${baseKey}-${subItem.title ?? ''}-${subIndex}`
                        return (
                          <NavigationMenuLink
                            key={subKey}
                            className="text-primary-blue hover:bg-pink-100"
                            asChild
                          >
                            <Link href={subItem.url || ''}>
                              {subItem.title}
                            </Link>
                          </NavigationMenuLink>
                        )
                      })}
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              )
            })}
          </NavigationMenuList>
        </NavigationMenu>
        <div className="flex shrink-0 items-center gap-2 xl:gap-4">
          {showContactUs && contactForm && (
            <RegisterInterestForm
              formTitle={contactForm?.formTitle || ''}
              formDescription={contactForm?.formSubTitle || ''}
            >
              <Button
                size="sm"
                variant="inverse"
                className="rounded-full font-bold"
              >
                {contactForm?.ctaTitle || 'Get in touch'}
              </Button>
            </RegisterInterestForm>
          )}
          {showCalendlyFlow && !careers && (
            <CalendlyFlowPopup>
              <Button
                size="sm"
                variant="inverse"
                className="rounded-full font-bold"
              >
                Enquire today
              </Button>
            </CalendlyFlowPopup>
          )}
          {showFamilyHub && (
            <Link href="/the-family-hub/">
              <Button
                size="sm"
                variant="inverse"
                className="rounded-full font-bold"
              >
                The Family Hub
              </Button>
            </Link>
          )}
          {showCareers && (
            <>
              {isUkSite() && !careers ? (
                <a href="https://www.nfamilycareers.com/">
                  <Button
                    size="sm"
                    variant="green"
                    className="rounded-full font-bold"
                  >
                    Careers at N
                  </Button>
                </a>
              ) : (
                <Link href={careers ? '/' : '/careers'}>
                  <Button
                    size="sm"
                    variant="green"
                    className="rounded-full font-bold"
                  >
                    {careers ? 'N Family' : 'Careers at N'}
                  </Button>
                </Link>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default function MastHeadRendererHovering({
  logo,
  mastheadNavigation,
  careers,
}: MastHeadRendererHoveringProps) {
  if (!logo || !mastheadNavigation) return null

  return (
    <>
      <div className="hidden w-full lg:block">
        <MastHeadHoveringDesktop
          logo={logo}
          mastheadNavigation={mastheadNavigation}
          careers={careers}
        />
      </div>
      <div className="block lg:hidden h-full">
        <MastHeadMobile
          logo={logo}
          mastheadNavigation={mastheadNavigation}
          careers={careers}
        />
      </div>
    </>
  )
}
